/**
 * Checkout Service
 * Handles widget checkout flow and payment processing
 */

import { apiClient, ApiClient } from './api';
import type { CartItem, ContactInfo, OrderConfirmation } from '../types/widget/order';
import type { PaymentIntent, CompleteCheckoutResponse } from '../types/widget/checkout';

/**
 * Session storage key for cart
 */
const CART_STORAGE_KEY = 'widget_cart';
const SESSION_ID_KEY = 'widget_checkout_session';

/**
 * CheckoutService class
 * Manages checkout sessions and payment processing
 */
export class CheckoutService {
  private client: ApiClient;

  constructor(client: ApiClient = apiClient) {
    this.client = client;
  }

  /**
   * Create a new checkout session
   * Sessions are server-side and tracked by sessionId
   */
  async createSession(providerSlug: string): Promise<{ sessionId: string }> {
    const response = await this.client.post<{ sessionId: string }>(
      `/widget/${providerSlug}/checkout/sessions`
    );

    // Store session ID in session storage
    sessionStorage.setItem(SESSION_ID_KEY, response.sessionId);

    return response;
  }

  /**
   * Get or create session
   * Returns existing session if valid, otherwise creates new one
   */
  async getOrCreateSession(providerSlug: string): Promise<string> {
    const existingSessionId = sessionStorage.getItem(SESSION_ID_KEY);

    if (existingSessionId) {
      // TODO: Validate session is still valid on backend
      return existingSessionId;
    }

    const { sessionId } = await this.createSession(providerSlug);
    return sessionId;
  }

  /**
   * Add item to checkout session
   */
  async addItem(providerSlug: string, sessionId: string, item: CartItem): Promise<void> {
    await this.client.post(
      `/widget/${providerSlug}/checkout/sessions/${sessionId}/items`,
      { item }
    );

    // Also store in session storage for client-side access
    this.saveCartToStorage(item);
  }

  /**
   * Set contact information for checkout
   */
  async setContactInfo(
    providerSlug: string,
    sessionId: string,
    contact: ContactInfo
  ): Promise<void> {
    await this.client.post(
      `/widget/${providerSlug}/checkout/sessions/${sessionId}/contact`,
      contact
    );
  }

  /**
   * Create payment intent for checkout
   * Returns Stripe client secret for payment
   */
  async createPaymentIntent(
    providerSlug: string,
    sessionId: string
  ): Promise<PaymentIntent> {
    const response = await this.client.post<PaymentIntent>(
      `/widget/${providerSlug}/checkout/sessions/${sessionId}/payment-intent`
    );

    return response;
  }

  /**
   * Complete checkout after successful payment
   * Creates order and optionally registers user account
   */
  async completeCheckout(
    providerSlug: string,
    sessionId: string,
    paymentIntentId: string,
    contactInfo: ContactInfo
  ): Promise<OrderConfirmation> {
    const response = await this.client.post<CompleteCheckoutResponse>(
      `/widget/${providerSlug}/checkout/sessions/${sessionId}/complete`,
      {
        paymentIntentId,
        contactInfo,
      }
    );

    // Clear session storage after successful checkout
    this.clearCart();
    sessionStorage.removeItem(SESSION_ID_KEY);

    return {
      orderId: response.orderId,
      orderNumber: response.orderNumber,
      status: 'pending',
      total: 0, // TODO: Get from payment intent
      userId: response.userId,
    };
  }

  /**
   * Save cart to session storage (client-side persistence)
   */
  saveCartToStorage(item: CartItem): void {
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(item));
  }

  /**
   * Load cart from session storage
   */
  loadCartFromStorage(): CartItem | null {
    const stored = sessionStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return null;

    try {
      return JSON.parse(stored) as CartItem;
    } catch {
      return null;
    }
  }

  /**
   * Clear cart from session storage
   */
  clearCart(): void {
    sessionStorage.removeItem(CART_STORAGE_KEY);
  }
}

// Export singleton instance
export const checkoutService = new CheckoutService();
